import React, { useState, useEffect, useMemo, useRef } from 'react';
import Select from 'react-select';
import DashboardLayout from './DashboardLayout';
import { useErrorAlert } from './AlertSystem';
import { blogsApi } from '../lib/api';
import { extractErrorMessage } from '../lib/utils';
import './DashboardBlogs.css';

interface Blog {
    id: number;
    title: string;
    slug: string;
    excerpt?: string;
    content: string;
    featuredImage?: string;
    category?: string;
    tags: string[];
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    views: number;
    publishedAt?: string;
    createdAt: string;
    updatedAt: string;
    author?: {
        id: number;
        name: string;
        email?: string;
    };
}

const DashboardBlogs: React.FC = () => {
    // Alert system hooks
    const showError = useErrorAlert();
    
    // Prevent multiple simultaneous fetches
    const isFetchingRef = useRef(false);

    const [allBlogs, setAllBlogs] = useState<Blog[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState<string | null>(null);
    const blogsPerPage = 12;

    // React-select options
    const categoryOptions = [
        { value: 'all', label: 'All Categories' },
        { value: 'Cybersecurity', label: 'Cybersecurity' },
        { value: 'Technology', label: 'Technology' },
        { value: 'Business', label: 'Business' },
        { value: 'Industry News', label: 'Industry News' },
        { value: 'Tutorials', label: 'Tutorials' },
        { value: 'Best Practices', label: 'Best Practices' },
        { value: 'Case Studies', label: 'Case Studies' }
    ];

    // Custom styles for react-select
    const selectStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.8)',
            border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(148, 163, 184, 0.2)'}`,
            borderRadius: '8px',
            padding: '2px 4px',
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            minHeight: '40px',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.15)' : 'none',
            cursor: 'pointer',
            '&:hover': {
                border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(99, 102, 241, 0.4)'}`
            }
        }),
        menu: (provided: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.95)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            zIndex: 99999999,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            background: state.isSelected 
                ? 'rgba(99, 102, 241, 0.2)' 
                : state.isFocused 
                ? 'rgba(255, 255, 255, 0.08)' 
                : 'transparent',
            color: state.isSelected ? 'var(--primary-purple)' : 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            padding: '10px 14px',
            cursor: 'pointer',
            '&:hover': {
                background: state.isSelected ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.08)'
            }
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        placeholder: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        input: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            padding: '6px',
            '&:hover': {
                color: 'var(--text-primary)'
            }
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Client-side filtering for better responsiveness
    const filteredBlogs = useMemo(() => {
        let filtered = allBlogs;

        // Apply search filter
        if (debouncedSearchQuery.trim()) {
            const query = debouncedSearchQuery.toLowerCase();
            filtered = filtered.filter(blog =>
                blog.title.toLowerCase().includes(query) ||
                blog.excerpt?.toLowerCase().includes(query) ||
                blog.content.toLowerCase().includes(query) ||
                blog.category?.toLowerCase().includes(query) ||
                blog.tags.some(tag => tag.toLowerCase().includes(query))
            );
        }

        // Apply category filter
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(blog => blog.category === categoryFilter);
        }

        return filtered;
    }, [allBlogs, debouncedSearchQuery, categoryFilter]);

    // Pagination
    const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);
    const startIndex = (currentPage - 1) * blogsPerPage;
    const paginatedBlogs = filteredBlogs.slice(startIndex, startIndex + blogsPerPage);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchQuery, categoryFilter]);

    // Initial data fetch
    useEffect(() => {
        const fetchBlogs = async () => {
            // Prevent multiple simultaneous fetches
            if (isFetchingRef.current) return;
            
            try {
                isFetchingRef.current = true;
                setLoading(true);
                setError(null);

                const response = await blogsApi.getPublishedBlogs({ limit: 1000 });

                if (response.success && response.data) {
                    // Handle both array and object with blogs property
                    const blogs = Array.isArray(response.data) ? response.data : (response.data.blogs || []);
                    setAllBlogs(blogs);
                } else {
                    throw new Error(extractErrorMessage(response));
                }
            } catch (error) {
                console.error('Error fetching published blogs:', error);
                setError(error instanceof Error ? error.message : 'Failed to fetch published blogs');
                showError(error instanceof Error ? error.message : 'Failed to fetch published blogs', 'Error Loading Published Blogs');
                setAllBlogs([]);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        };

        fetchBlogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only fetch once on mount

    const formatDate = (dateString: string) => {
        if (!dateString) return 'Never';
        
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getTimeAgo = (dateString: string) => {
        if (!dateString) return 'Never';
        
        const date = new Date(dateString);
        const now = new Date();
        
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInMinutes < 1) {
            return 'Just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        } else if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        } else if (diffInDays < 7) {
            return `${diffInDays}d ago`;
        } else {
            return formatDate(dateString);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'PUBLISHED': return '#10b981';
            case 'DRAFT': return '#f59e0b';
            case 'ARCHIVED': return '#6b7280';
            default: return '#6b7280';
        }
    };

    const handleBlogClick = (blog: Blog) => {
        setSelectedBlog(blog);
        setIsDetailModalOpen(true);
    };

    const truncateText = (text: string, maxLength: number) => {
        if (text.length <= maxLength) return text;
        return text.substr(0, maxLength) + '...';
    };

    if (loading) {
        return (
            <DashboardLayout title="Blog Posts">
                <div className="dashboard-blogs-loading">
                    <div className="dashboard-blogs-loading-spinner">
                        <i className="ri-loader-4-line"></i>
                    </div>
                    <div className="dashboard-blogs-loading-text">Loading blog posts...</div>
                </div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout title="Blog Posts">
                <div className="dashboard-blogs-error">
                    <div className="dashboard-blogs-error-icon">
                        <i className="ri-error-warning-line"></i>
                    </div>
                    <div className="dashboard-blogs-error-content">
                        <h3>Failed to Load Blog Posts</h3>
                        <p>{error}</p>
                        <button
                            className="dashboard-blogs-retry-btn"
                            onClick={() => window.location.reload()}
                        >
                            <i className="ri-refresh-line"></i>
                            Try Again
                        </button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Blog Posts">
            <div className="dashboard-blogs">
                {/* Header */}
                <div className="dashboard-blogs-header">
                    <div className="dashboard-blogs-header-content">
                        <h1 className="dashboard-blogs-title">Blog Posts</h1>
                        <p className="dashboard-blogs-subtitle">
                            Stay updated with the latest insights, tutorials, and industry news from our cybersecurity experts
                        </p>
                    </div>
                    <div className="dashboard-blogs-stats">
                        <div className="dashboard-blogs-stat">
                            <div className="dashboard-blogs-stat-value">{allBlogs.length}</div>
                            <div className="dashboard-blogs-stat-label">Total Posts</div>
                        </div>
                        <div className="dashboard-blogs-stat">
                            <div className="dashboard-blogs-stat-value">
                                {allBlogs.filter((b: Blog) => b.status === 'PUBLISHED').length}
                            </div>
                            <div className="dashboard-blogs-stat-label">Published</div>
                        </div>
                        <div className="dashboard-blogs-stat">
                            <div className="dashboard-blogs-stat-value">
                                {allBlogs.reduce((acc, blog) => acc + blog.views, 0)}
                            </div>
                            <div className="dashboard-blogs-stat-label">Total Views</div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="dashboard-blogs-filters">
                    <div className="dashboard-blogs-filters-row">
                        <div className="dashboard-blogs-search-container">
                            <div className="dashboard-blogs-search">
                                <i className="ri-search-line"></i>
                                <input
                                    type="text"
                                    placeholder="Search blog posts by title, content, category, or tags..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`dashboard-blogs-search-input ${debouncedSearchQuery !== searchQuery ? 'searching' : ''}`}
                                />
                                {searchQuery && (
                                    <button
                                        className="dashboard-blogs-search-clear"
                                        onClick={() => setSearchQuery('')}
                                        title="Clear search"
                                    >
                                        <i className="ri-close-line"></i>
                                    </button>
                                )}
                                {debouncedSearchQuery !== searchQuery && (
                                    <div className="dashboard-blogs-search-loading">
                                        <i className="ri-loader-4-line rotating"></i>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="dashboard-blogs-filters-secondary">
                        <div className="dashboard-blogs-filter-group">
                            <div className="dashboard-blogs-filter-item">
                                <label className="dashboard-blogs-filter-label">Category</label>
                                <Select
                                    options={categoryOptions}
                                    value={categoryOptions.find(option => option.value === categoryFilter)}
                                    onChange={(option) => setCategoryFilter(option?.value || 'all')}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="dashboard-blogs-filter-select"
                                />
                            </div>
                            
                            <div className="dashboard-blogs-filter-actions">
                                <button
                                    className="dashboard-blogs-filter-clear"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setCategoryFilter('all');
                                    }}
                                    title="Clear all filters"
                                >
                                    <i className="ri-filter-off-line"></i>
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Blog Grid */}
                <div className="dashboard-blogs-grid-container">
                    {paginatedBlogs.length === 0 ? (
                        <div className="dashboard-blogs-empty">
                            <i className="ri-article-line"></i>
                            <h3>No blog posts found</h3>
                            <p>Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        <div className="dashboard-blogs-grid">
                            {paginatedBlogs.map((blog) => (
                                <div key={blog.id} className="dashboard-blog-card" onClick={() => handleBlogClick(blog)}>
                                    {blog.featuredImage && (
                                        <div className="dashboard-blog-image">
                                            <img src={blog.featuredImage} alt={blog.title} />
                                            <div className="dashboard-blog-status-overlay">
                                                <span
                                                    className="dashboard-blog-status-badge"
                                                    style={{
                                                        backgroundColor: `${getStatusColor(blog.status)}20`,
                                                        color: getStatusColor(blog.status)
                                                    }}
                                                >
                                                    {blog.status.toLowerCase()}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="dashboard-blog-content">
                                        <div className="dashboard-blog-meta-top">
                                            {blog.category && (
                                                <span className="dashboard-blog-category">{blog.category}</span>
                                            )}
                                            <span className="dashboard-blog-date">{getTimeAgo(blog.createdAt)}</span>
                                        </div>
                                        <h3 className="dashboard-blog-title">{blog.title}</h3>
                                        <p className="dashboard-blog-excerpt">
                                            {blog.excerpt || truncateText(blog.content.replace(/<[^>]*>/g, ''), 120)}
                                        </p>
                                        <div className="dashboard-blog-meta-bottom">
                                            <div className="dashboard-blog-author">
                                                <i className="ri-user-line"></i>
                                                <span>{blog.author?.name || 'Unknown Author'}</span>
                                            </div>
                                            <div className="dashboard-blog-views">
                                                <i className="ri-eye-line"></i>
                                                <span>{blog.views}</span>
                                            </div>
                                        </div>
                                        {blog.tags.length > 0 && (
                                            <div className="dashboard-blog-tags">
                                                {blog.tags.slice(0, 3).map((tag, index) => (
                                                    <span key={index} className="dashboard-blog-tag">
                                                        {tag}
                                                    </span>
                                                ))}
                                                {blog.tags.length > 3 && (
                                                    <span className="dashboard-blog-tag-more">
                                                        +{blog.tags.length - 3}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="dashboard-blogs-pagination">
                        <button
                            className="dashboard-blogs-pagination-btn"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            <i className="ri-arrow-left-line"></i>
                            Previous
                        </button>
                        <div className="dashboard-blogs-pagination-info">
                            Page {currentPage} of {totalPages} ({filteredBlogs.length} posts)
                        </div>
                        <button
                            className="dashboard-blogs-pagination-btn"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                            <i className="ri-arrow-right-line"></i>
                        </button>
                    </div>
                )}

                {/* Blog Detail Modal */}
                {isDetailModalOpen && selectedBlog && (
                    <div className="dashboard-blogs-modal-overlay" onClick={() => setIsDetailModalOpen(false)}>
                        <div className="dashboard-blogs-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="dashboard-blogs-modal-header">
                                <div className="dashboard-blogs-modal-header-content">
                                    <h3>{selectedBlog.title}</h3>
                                    <div className="dashboard-blogs-modal-meta">
                                        <span className="dashboard-blogs-modal-author">
                                            <i className="ri-user-line"></i>
                                            {selectedBlog.author?.name || 'Unknown Author'}
                                        </span>
                                        <span className="dashboard-blogs-modal-date">
                                            <i className="ri-calendar-line"></i>
                                            {formatDate(selectedBlog.createdAt)}
                                        </span>
                                        <span className="dashboard-blogs-modal-views">
                                            <i className="ri-eye-line"></i>
                                            {selectedBlog.views} views
                                        </span>
                                        <span
                                            className="dashboard-blogs-modal-status"
                                            style={{
                                                backgroundColor: `${getStatusColor(selectedBlog.status)}20`,
                                                color: getStatusColor(selectedBlog.status)
                                            }}
                                        >
                                            {selectedBlog.status.toLowerCase()}
                                        </span>
                                    </div>
                                </div>
                                <button onClick={() => setIsDetailModalOpen(false)}>
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div className="dashboard-blogs-modal-content">
                                {selectedBlog.featuredImage && (
                                    <div className="dashboard-blogs-modal-image">
                                        <img src={selectedBlog.featuredImage} alt={selectedBlog.title} />
                                    </div>
                                )}
                                {selectedBlog.excerpt && (
                                    <div className="dashboard-blogs-modal-excerpt">
                                        <h4>Summary</h4>
                                        <p>{selectedBlog.excerpt}</p>
                                    </div>
                                )}
                                <div className="dashboard-blogs-modal-body">
                                    <h4>Content</h4>
                                    <div className="dashboard-blogs-modal-text" dangerouslySetInnerHTML={{ __html: selectedBlog.content }} />
                                </div>
                                {selectedBlog.tags.length > 0 && (
                                    <div className="dashboard-blogs-modal-tags">
                                        <h4>Tags</h4>
                                        <div className="dashboard-blogs-modal-tags-list">
                                            {selectedBlog.tags.map((tag, index) => (
                                                <span key={index} className="dashboard-blogs-modal-tag">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default DashboardBlogs;